import {useObvioUser} from 'obvio/auth'
import useDebounce from 'lib/debounce'
import {api} from 'lib/url'
import {teamMemberClient} from 'obvio/obvio-client'
import {Organization} from 'organization'
import React, {useEffect, useState, useCallback} from 'react'
import {sendRequest} from 'obvio/Organizations/CreateOrganizationForm'
import {PaginatedCollection} from 'lib/ui/api-client'

const PER_PAGE = 12
export type PerPageOption = 12 | 24 | 48 | 100 | 999999

interface UserOrganizationsContextProps {
  organizations: Organization[]
  loading: boolean
  searchQuery: string
  setSearchQuery: (query: string) => void
  page: number
  setPage: (page: number) => void
  perPage: PerPageOption
  setPerPage: (perPage: PerPageOption) => void
  lastPage: number
  error: string | null
  clearError: () => void
}

const UserOrganizationsContext = React.createContext<
  UserOrganizationsContextProps | undefined
>(undefined)

export default function UserOrganizationsProvider(props: {
  children: React.ReactNode
}) {
  const [organizations, setOrganizations] = useState<Organization[] | null>(
    null,
  )

  const [searchQuery, setSearchQueryState] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 400)

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState<PerPageOption>(PER_PAGE)
  const [lastPage, setLastPage] = useState(1)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const clearError = () => setError(null)

  const {
    has_active_subscription: isSubscribed,
    first_name,
    last_name,
  } = useObvioUser()

  const newOrganizationName = `${first_name} ${last_name}'s Organization`

  const setSearchQuery = useCallback((query: string) => {
    setSearchQueryState(query)
    setPage(1)
  }, [])

  const createOrganization = useCallback(() => {
    const orgData = {
      name: newOrganizationName,
      logo: null,
    }

    sendRequest(orgData)
      .then((data) => {
        setOrganizations([data])
      })
      .catch((error) => {
        console.error(error)
      })
  }, [newOrganizationName])

  useEffect(() => {
    setLoading(true)
    loadOrganizations(page, perPage, debouncedSearchQuery)
      .then((organizations) => {
        // Auto-create an organization if a user is subscribed, and they
        // haven't created on already.
        const shouldAutoCreateOrganization =
          isSubscribed && organizations.total === 0 && !debouncedSearchQuery
        if (shouldAutoCreateOrganization) {
          createOrganization()
          return
        }

        clearError()
        setOrganizations(organizations.data)
        setLastPage(organizations.last_page)
        setPage(organizations.current_page)
      })
      .catch(() => {
        setError('An error occurred while fetching organizations')
        setOrganizations([])
      })
      .finally(() => {
        setLoading(false)
      })
  }, [createOrganization, isSubscribed, page, perPage, debouncedSearchQuery])

  return (
    <UserOrganizationsContext.Provider
      value={{
        loading: organizations === null || loading,
        organizations: organizations || [],
        searchQuery,
        setSearchQuery,
        page,
        setPage,
        perPage,
        setPerPage,
        lastPage,
        error,
        clearError,
      }}
    >
      {props.children}
    </UserOrganizationsContext.Provider>
  )
}

export function useUserOrganizations() {
  const context = React.useContext(UserOrganizationsContext)
  if (context === undefined) {
    throw new Error(
      `useUserOrganizations must be used within an UserOrganizationsProvier`,
    )
  }

  return context
}

function loadOrganizations(
  page: number,
  perPage: PerPageOption,
  searchQuery: string,
) {
  const params = new URLSearchParams()
  params.set('page', `${page}`)
  params.set('per_page', `${perPage}`)
  const trimmed = searchQuery.trim()
  if (trimmed) {
    params.set('search', trimmed)
  }

  return teamMemberClient.get<PaginatedCollection<Organization>>(
    api(`/organizations?${params.toString()}`),
  )
}
